import type { UploadUrlRequest, UploadUrlResponse } from "schema/asset";
import { computeFileHash } from "./hash";
import type { AssetFile } from "./types";

/**
 * Requests a signed URL from the backend for direct GCS upload.
 */
async function getUploadUrl(request: UploadUrlRequest): Promise<UploadUrlResponse> {
  const response = await fetch("/api/assets/upload-url", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ code: "UNKNOWN" }));
    throw new Error(error.code || `Upload URL request failed: ${response.status}`);
  }

  return response.json();
}

/**
 * Uploads a file directly to GCS using a signed URL.
 * Returns the public URL on success.
 */
async function uploadToGCS(uploadUrl: string, file: Blob, contentType: string): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`GCS upload failed: ${response.status}`);
  }
}

/**
 * Uploads an asset to GCS and returns the public URL.
 * This function:
 * 1. Computes SHA-256 hash of the file (in Web Worker)
 * 2. Requests a signed URL from the backend
 * 3. Skips upload if file already exists (same content hash)
 * 4. Uploads the file directly to GCS if needed
 * 5. Returns the public URL
 */
export async function uploadAssetToCloud(asset: AssetFile, file: Blob): Promise<string> {
  // Convert Blob to File for hash computation (File extends Blob)
  const fileForHash =
    file instanceof File ? file : new File([file], asset.name, { type: asset.mimeType });
  const contentHash = await computeFileHash(fileForHash);

  const { uploadUrl, publicUrl, exists } = await getUploadUrl({
    filename: contentHash,
    contentType: asset.mimeType,
    size: file.size,
  });

  // Skip upload if file already exists in GCS
  if (!exists) {
    await uploadToGCS(uploadUrl, file, asset.mimeType);
  }

  return publicUrl;
}
